import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { useAccount } from '@hcs/authn';
import { QUERY_KEY_CAPABILITIES_FOR_USER } from '@hcs/authz';
import { queryClient } from '@hcs/http-clients';
import { SelfServeOrgInfo, SelfServeOrgInfoResponse } from '@hcs/types';
import { processSelfServePlan } from '@hcs/utils';
import { hookEnabledError } from '@hcs/utils';

import { OrgSelfServeInfoApi } from '../api';

export const QUERY_KEY_ORG_SELF_SERVE_INFO = 'QUERY_KEY_ORG_SELF_SERVE_INFO';
export const QUERY_KEY_ORG_SELF_SERVE_INFO_INTERNAL =
  'QUERY_KEY_ORG_SELF_SERVE_INFO_INTERNAL';
const augmentOrgInfo = (
  orgSsInfo: SelfServeOrgInfoResponse,
): SelfServeOrgInfo => {
  const activePlan = orgSsInfo?.active_plan;
  const activeUntil = activePlan?.active_until;
  const currentPlan = orgSsInfo?.self_serve_plan;
  const upcomingPlan = orgSsInfo?.upcoming_self_serve_plan;
  return {
    ...orgSsInfo,
    self_serve_plan:
      currentPlan && processSelfServePlan(currentPlan, orgSsInfo.capabilities),
    upcoming_self_serve_plan:
      upcomingPlan &&
      processSelfServePlan(upcomingPlan, orgSsInfo.capabilities),
    canceled_subscription:
      !!(activeUntil && !upcomingPlan) ||
      // user has cancelled and their subscription has expired
      !!(orgSsInfo.self_serve_status === 'paid' && !activePlan),
    changed_plan: !!(
      activeUntil &&
      upcomingPlan &&
      (upcomingPlan.name !== currentPlan?.name ||
        upcomingPlan.commit_period !== currentPlan?.commit_period)
    ),
  };
};
export const useOrgSelfServeInfo = (
  options?: Partial<
    UseQueryOptions<
      SelfServeOrgInfo | null,
      unknown,
      SelfServeOrgInfo | null,
      [string]
    >
  >,
) => {
  const accountQuery = useAccount();
  return useQuery<
    SelfServeOrgInfo | null,
    unknown,
    SelfServeOrgInfo | null,
    [string]
  >({
    queryKey: [QUERY_KEY_ORG_SELF_SERVE_INFO],
    queryFn: async () => {
      try {
        const orgSsInfo = (await OrgSelfServeInfoApi.fetchOrgSelfServeInfo())
          .data;
        return augmentOrgInfo(orgSsInfo);
      } catch {
        return null;
      }
    },
    ...options,
    enabled: options?.enabled === false ? false : !!accountQuery.data,
  });
};

export const useOrgSelfServeInfoInternal = (
  orgId: number | undefined,
  options?: Partial<
    UseQueryOptions<
      SelfServeOrgInfo | null,
      unknown,
      SelfServeOrgInfo | null,
      [string, number | undefined]
    >
  >,
) => {
  return useQuery<
    SelfServeOrgInfo | null,
    unknown,
    SelfServeOrgInfo | null,
    [string, number | undefined]
  >({
    queryKey: [QUERY_KEY_ORG_SELF_SERVE_INFO_INTERNAL, orgId],
    queryFn: async () => {
      try {
        if (!orgId) {
          throw hookEnabledError('useOrgSelfServeInfoInternal', 'orgId');
        }
        const orgSsInfo =
          await OrgSelfServeInfoApi.fetchOrgSelfServeInfoInternal(orgId);
        // Invalidate capabilities query cache for the org following a successful plan creation
        // (which follows a successful Stripe checkout)
        if (orgSsInfo.self_serve_plan) {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_CAPABILITIES_FOR_USER],
          });
        }
        return augmentOrgInfo(orgSsInfo);
      } catch {
        return null;
      }
    },
    ...options,
    enabled: options?.enabled === false ? false : !!orgId,
  });
};
